import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Card, CardBody } from '@structural-climbing/ui'
import { GymDialog } from '../../components/GymDialog'
import { NavIcon } from '../../layouts/NavIcon'
import { QrScanner } from './QrScanner'

interface QuickScanValue {
  /** True when this person may check members in. */
  canScan: boolean
  open: () => void
}

const QuickScanContext = createContext<QuickScanValue>({ canScan: false, open: () => {} })

/** The phone's full-screen scanner, openable from any page. */
export function useQuickScan(): QuickScanValue {
  return useContext(QuickScanContext)
}

/**
 * Holds the full-screen camera scanner a phone opens from the floating button,
 * or the Check-in card. The camera starts as it opens and
 * stays ready for the next member; closing it stops the camera.
 */
export function QuickScanProvider({
  canScan,
  children,
}: {
  canScan: boolean
  children: ReactNode
}) {
  const { t } = useTranslation()
  const [isOpen, setOpen] = useState(false)
  const open = useCallback(() => setOpen(true), [])
  const value = useMemo(() => ({ canScan, open }), [canScan, open])

  return (
    <QuickScanContext.Provider value={value}>
      {children}
      {isOpen && (
        <GymDialog onClose={() => setOpen(false)} showCloseButton>
          <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
            <h2 className="text-lg font-semibold">{t('scan.quickTitle')}</h2>
            <QrScanner debug={false} autoStartCamera source="quick-scan" />
          </div>
        </GymDialog>
      )}
    </QuickScanContext.Provider>
  )
}

/** A large "Scan a member" card for the top of a phone page. */
export function ScanCard({ title, hint }: { title: string; hint: string }) {
  const { open } = useQuickScan()
  return (
    <Card
      isPressable
      onPress={open}
      shadow="md"
      className="mb-4 w-full bg-primary text-left text-primary-foreground"
    >
      <CardBody className="flex flex-row items-center gap-3 px-4 py-3.5">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/20">
          <NavIcon name="qr" size={22} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[15px] font-semibold">{title}</span>
          <span className="block text-xs opacity-85">{hint}</span>
        </span>
        <span aria-hidden="true" className="text-xl">
          ›
        </span>
      </CardBody>
    </Card>
  )
}

/** The phone's floating scan button, bottom right, clear of the home indicator. */
export function ScanFab() {
  const { t } = useTranslation()
  const { open } = useQuickScan()
  return (
    <Button
      isIconOnly
      color="primary"
      aria-label={t('scan.quickTitle')}
      onPress={open}
      className="fixed bottom-[calc(1.25rem+env(safe-area-inset-bottom,0px))] right-4 z-40 h-14 w-14 rounded-2xl shadow-lg shadow-primary/40"
    >
      <NavIcon name="qr" size={24} />
    </Button>
  )
}
